import PropTypes from 'prop-types';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import Modal from 'react-modal';
import { ButtonTypes, StandardButton } from '@eika/button';
import Question from './question';
import AHVContext from './ahv-context';
import { AktivVurdering } from './ahv-schema';
import { emptyList } from './constants';
import {
    getCountryOptionsList,
    getQuestionList,
    prefillValuesWithAnswersFromQuestionList,
    updateSkjemaWithAnswers,
} from './form-util';
import HelpModal from './help-modal';
import { validateForm } from './validator';
import CountryContext from './country-context';

function KDKForm({
    questions,
    landOptionLists,
    onSubmit,
    onAbort,
    rotNodeId,
    answers,
    rolle,
    children,
    displayNesteButton,
    isTheLastButton,
    norwayPostalCodesList,
    submitButtonText = 'Lagre',
    abortButtonText = 'Avbryt',
    appNamespace,
    useCustomSubmitButtons = false,
}) {
    useEffect(() => {
        Modal.setAppElement(rotNodeId);
    }, [rotNodeId]);

    const questionList = useMemo(() => getQuestionList(questions), [questions]);

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [scrollViewUpdated, updateScrollView] = useState(false);

    const [values, setValues] = useState(
        answers && Object.keys(answers).length ? answers : prefillValuesWithAnswersFromQuestionList(questionList)
    );

    const hasSkjemaInfoModal = questions.skjema && questions.skjema.skjemaInfoModal;

    const allCountries = landOptionLists ? landOptionLists.alleLand : emptyList;
    const excludeListe = landOptionLists ? landOptionLists.excludeListe : null;

    const getOptionsList = useCallback(
        (type) => () => getCountryOptionsList(allCountries, excludeListe, type),
        [allCountries, excludeListe]
    );

    useEffect(() => {
        const firstError = document.querySelectorAll('.input-error, .error')[0];
        if (firstError) {
            firstError.scrollIntoView();
        }
    }, [scrollViewUpdated, answers]);

    const getCountryContextVal = useMemo(
        () => ({
            allCountries,
            excludeListe,
            getCRSOptions: getOptionsList('CRS'),
            getBostedslandOptions: getOptionsList('BOSTEDSLAND'),
            getFodelandOptions: getOptionsList('FODELAND'),
            getStatsborgerskapOptions: getOptionsList('STATSBORGERSKAP'),
            getTransaksjonerOptions: getOptionsList('TRANSAKSJONER'),
            getNorwegianPostalCodesOptions: norwayPostalCodesList,
        }),
        [allCountries, excludeListe, getOptionsList, norwayPostalCodesList]
    );

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            setHasSubmitted(true);
            updateScrollView((scrollViewUpdated) => !scrollViewUpdated);
            const renderedQuestions = getQuestionList(questions);
            const isValid = validateForm(values, renderedQuestions, norwayPostalCodesList);
            if (isValid) {
                const updatedSkjema = updateSkjemaWithAnswers(questions, values, renderedQuestions);
                onSubmit(updatedSkjema, values);
            }
        },
        [norwayPostalCodesList, onSubmit, questions, values]
    );

    const getQuestionValue = useCallback(
        (definisjon) => {
            return values[definisjon] || { error: true };
        },
        [values]
    );

    const onChange = useCallback((definisjon, value) => {
        setValues((oldVals) => {
            const newState = {
                ...(oldVals[definisjon] || { error: true }),
                ...value,
            };
            return {
                ...oldVals,
                [definisjon]: newState,
            };
        });
    }, []);

    const getHasSubmitted = useCallback(() => hasSubmitted, [hasSubmitted]);
    const remove = useCallback((definisjon) => {
        setValues((oldVals) => {
            const newState = oldVals;
            delete newState[definisjon];
            return newState;
        });
    }, []);

    const updateValidation = useCallback((definisjon, touched, error) => {
        setValues((oldVals) => {
            const newState = {
                ...(oldVals[definisjon] || { error: true }),
                touched,
                error,
            };
            return {
                ...oldVals,
                [definisjon]: newState,
            };
        });
    }, []);

    const hasError = useCallback(
        (definisjon) => {
            const { touched, error } = getQuestionValue(definisjon);
            return (hasSubmitted || touched) && error;
        },
        [hasSubmitted, getQuestionValue]
    );

    const handleAbort = useCallback(
        (event) => {
            event.preventDefault();
            onAbort();
        },
        [onAbort]
    );

    const getValue = useCallback(
        (definisjon) => {
            const { value } = getQuestionValue(definisjon);
            if (value === 0) {
                return value;
            }
            return value || emptyList;
        },
        [getQuestionValue]
    );

    const removeValue = useCallback(
        (definisjon) => {
            setValues((oldValues) => {
                const newValues = { ...oldValues };
                delete newValues[definisjon];
                return newValues;
            });
        },
        [setValues]
    );

    const touched = useCallback((definisjon) => getQuestionValue(definisjon).touched || false, [getQuestionValue]);

    const getRolle = useCallback(() => rolle, [rolle]);

    const getValidateIfNotSet = useCallback(
        (question) => {
            if (question.validateIfNotSet && question.validateIfNotSet.length > 0) {
                return values[question.validateIfNotSet[0].definisjon];
            }
            return values[question.definisjon];
        },
        [values]
    );

    const getAHVContext = useMemo(
        () => ({
            onChange,
            remove,
            updateValidation,
            getValue,
            removeValue,
            touched,
            hasError,
            getHasSubmitted,
            getRolle,
        }),
        [onChange, remove, updateValidation, getValue, removeValue, touched, hasError, getHasSubmitted, getRolle]
    );

    {
        const buttons =
            children ||
            ((handleSubmit) => (
                <div className="submitting-buttons">
                    {onAbort && (
                        <StandardButton onClick={handleAbort} buttonType={ButtonTypes.STANDARD_NEGATIVE}>
                            {abortButtonText}
                        </StandardButton>
                    )}
                    <StandardButton onClick={handleSubmit}>{submitButtonText}</StandardButton>
                </div>
            ));

        return (
            <CountryContext.Provider value={getCountryContextVal}>
                <AHVContext.Provider value={getAHVContext}>
                    <form
                        onSubmit={(event) => {
                            if (useCustomSubmitButtons) {
                                handleSubmit(event);
                            } else {
                                event.preventDefault();
                            }
                        }}
                        className="kdk-form"
                    >
                        {hasSkjemaInfoModal && (
                            <div className="erklaringsinfo">
                                <HelpModal question={questions.skjema.skjemaInfoModal} appNamespace={appNamespace} />
                            </div>
                        )}
                        {questionList.map((question) => (
                            <Question
                                key={`question_${question.definisjon}`}
                                question={question}
                                questionState={values[question.definisjon]}
                                validateIfNotSet={getValidateIfNotSet(question)}
                                hasError={hasError(question.definisjon)}
                                appNamespace={appNamespace}
                            />
                        ))}
                        {displayNesteButton ? (
                            <>
                                {children}
                                <StandardButton
                                    className="progress-next"
                                    onClick={handleSubmit}
                                    buttonType={ButtonTypes.STANDARD_POSITIVE}
                                >
                                    <span>{isTheLastButton ? 'Send inn' : 'Neste'}</span>
                                </StandardButton>
                            </>
                        ) : (
                            buttons
                        )}
                    </form>
                </AHVContext.Provider>
            </CountryContext.Provider>
        );
    }
}

const LandOptionsListsShape = {
    alleLand: PropTypes.array.isRequired,
    excludeListe: PropTypes.shape({
        BOSTEDSLAND: PropTypes.array,
        CRS: PropTypes.array,
        FODELAND: PropTypes.array,
        STATSBORGERSKAP: PropTypes.array,
        TRANSAKSJONER: PropTypes.array,
    }).isRequired,
};

KDKForm.propTypes = {
    questions: PropTypes.shape(AktivVurdering).isRequired,
    landOptionLists: PropTypes.shape(LandOptionsListsShape),
    onSubmit: PropTypes.func,
    onAbort: PropTypes.func,
    rotNodeId: PropTypes.string.isRequired,
    answers: PropTypes.object,
    rolle: PropTypes.string,
    displayNesteButton: PropTypes.bool,
    isTheLastButton: PropTypes.bool,
    submitButtonText: PropTypes.string,
    abortButtonText: PropTypes.string,
    appNamespace: PropTypes.string.isRequired,
};

export default KDKForm;
